import React, { useEffect, useMemo, useState, useRef } from 'react';
import useAppStore from '../../store/useAppStore.js';
import TableCard from './tableCard.js';
import TablesList from './tablesList.js';
import {
  fetchTables,
  createTable as createTableApi,
  activateTable as activateTableApi,
  deactivateTable as deactivateTableApi,
  deleteTable as deleteTableApi
} from '../../services/tableApi.js';
import { cancelOrder, completeOrder } from '../../services/orderApi.js';
import { io } from 'socket.io-client';
import socketConfig from '../../config/socketconfig.js';
import useOrderStore from '../../store/branchstore/branchOrdersStore.js';

import FormInput from '../common/FormInput.js';
import SelectField from '../common/SelectField.js';

const TablesSection = ({ selectedTable, onCreateTable, onActivateTable, onSelectTable, onPrintTable, onCancelTable, onRemoveTable, updatePrintMode }) => {
  const [newTableName, setNewTableName] = useState('');
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const socketRef = useRef(null);

  const {
    branchTables,
    setTables,
    activateTable: activateTableStore,
    deactivateTable: deactivateTableStore,
    removeTable: removeTableStore,
    cancelTableOrder: cancelTableOrderStore,
    updatePaymentInfo: updatePaymentInfo,
    tablePaymentInfo: tablePaymentInfo,
    tablesLoaded,
    offlineMode
  } = useAppStore((state) => ({
    branchTables: state.branchTables,
    setTables: state.setTables,
    activateTable: state.activateTable,
    deactivateTable: state.deactivateTable,
    removeTable: state.removeTable,
    cancelTableOrder: state.cancelTableOrder,
    updatePaymentInfo: state.updatePaymentInfo,
    tablePaymentInfo: state.tablePaymentInfo,
    tablesLoaded: state.tablesLoaded
  }));

  const updateOrderStatus = useOrderStore((state) => state.updateOrderStatus);
  const loadTables = async () => {
    setLoading(true);
    try {
      const response = await fetchTables();
      const apiTables = response.tables || [];
      setTables(apiTables);
      setFeedback('');
    } catch (error) {
      setFeedback(error.message || 'Unable to load tables.');
    } finally {
      setLoading(false);
    }
  };

  const handleDiscountChange = (amount) => {
    const discount = parseFloat(amount) || 0;
    updatePaymentInfo({ discount });
  };

  const handleReceivedCashChange = (amount) => {
    const receivedCash = parseFloat(amount) || 0;
    updatePaymentInfo({ receivedCash });
  };

  const handlePaymentTypeChange = (paymentType) => {
    updatePaymentInfo({ paymentType });
  };

  useEffect(() => {
    // Only load tables if they haven't been loaded yet
    if (!tablesLoaded && branchTables.length === 0) {
      loadTables();
    }

    if (offlineMode) {
      return;
    }

    // Initialize Socket.io connection for real-time updates
    const token = localStorage.getItem('token');
    if (token) {
      const socketUrl = socketConfig.SOCKET_URL;
      socketRef.current = io(socketUrl, {
        path: '/socket.io',
        transports: ['websocket', 'polling'],
        auth: {
          token
        }
      });

      socketRef.current.on('connect_error', (error) => {
        console.error('TablesSection socket connection error:', error);
      });

      // Listen for order updates
      socketRef.current.on('order_updated', (data) => {
        if (data.status === 'completed' || data.status === 'cancelled') {
          // Refresh tables when order is completed/cancelled
          loadTables();
        }
      });

      // Listen for token events
      socketRef.current.on('token_event', (data) => {
        if (data.event && (data.event.status === 'completed' || data.event.status === 'cancelled')) {
          // Refresh tables when token is completed/cancelled
          loadTables();
        }
      });

      return () => {
        if (socketRef.current) {
          socketRef.current.disconnect();
        }
      };
    }
  }, [branchTables.length, tablesLoaded, offlineMode]);

  const handleActivateTable = async (tableId) => {
    setLoading(true);
    try {
      await activateTableApi(tableId);
      activateTableStore(tableId);
      setFeedback('Table activated successfully.');
      await loadTables();
      if (onActivateTable) onActivateTable(tableId);
    } catch (error) {
      setFeedback(error.message || 'Unable to activate table.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivateTable = async (tableId) => {
    setLoading(true);
    try {
      await deactivateTableApi(tableId);
      deactivateTableStore(tableId);
      setFeedback('Table deactivated successfully.');
      await loadTables();
    } catch (error) {
      setFeedback(error.message || 'Unable to deactivate table.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTable = async () => {
    if (!newTableName.trim()) {
      setFeedback('Please enter a table name.');
      return;
    }

    setLoading(true);
    try {
      await createTableApi({ name: newTableName.trim() });
      setFeedback('Table created successfully.');
      setNewTableName('');
      await loadTables();
      if (onCreateTable) {
        onCreateTable(newTableName.trim());
      }
    } catch (error) {
      setFeedback(error.message || 'Unable to create table.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTable = async (tableId) => {
    setLoading(true);
    try {
      await deleteTableApi(tableId);
      removeTableStore(tableId);
      setFeedback('Table removed successfully.');
      if (onRemoveTable) {
        onRemoveTable(tableId);
      }
      await loadTables();
    } catch (error) {
      setFeedback(error.message || 'Unable to remove table.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelTable = async (tableId, orderId) => {
    setLoading(true);
    try {
      if (orderId) {
        await cancelOrder(orderId);
        updateOrderStatus(orderId, { status: 'cancelled' });
      }
      cancelTableOrderStore(tableId);
      setFeedback('Table order cancelled and table deactivated.');
      if (onCancelTable) {
        onCancelTable(tableId);
      }
      await loadTables();
    } catch (error) {
      setFeedback(error.message || 'Unable to cancel the table order.');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = (table) => {
    if (!table || !table.items || table.items.length === 0) {
      setFeedback('There are no items to print for this table.');
      return;
    }
    updatePrintMode('receipt');
    if (onPrintTable) {
      onPrintTable(table, 'receipt', true);
    }
  };

  const handleComplete = async (table) => {
    if (!table || !table.order_id) {
      setFeedback('No order associated with this table.');
      return;
    }

    setLoading(true);
    try {
      await completeOrder(table.order_id);
      updateOrderStatus(table.order_id, { status: 'completed' });
      setFeedback('Order completed successfully. Table is now free.');
      await loadTables();
    } catch (error) {
      setFeedback(error.message || 'Unable to complete the order.');
    } finally {
      setLoading(false);
    }
  };

  const activeTables = useMemo(
    () => branchTables.filter((table) => table.items && table.items.length > 0 && table.is_active),
    [branchTables]
  );

  return (
    <div className="space-y-6">
      <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-2xl font-semibold">Table Orders</h2>
            <p className="mt-2 text-sm text-gray-500">Manage all tables vertically and keep orders active until completion.</p>
          </div>
          <div className="text-sm text-gray-600">{activeTables.length} active table(s)</div>
        </div>

        {feedback && (
          <div className="mb-4 rounded-2xl bg-blue-50 p-3 text-sm text-blue-700">{feedback}</div>
        )}

        {loading ? (
          <div className="text-gray-600">Loading tables...</div>
        ) : activeTables.length > 0 ? (
          <div>
            {/* <div className="mb-4 flex gap-4 items-start">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search tables by name..."
                className="rounded-2xl border border-gray-300 bg-white px-4 py-3 text-sm text-gray-700 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
              />
              <div>
                <FormInput 
                  placeholder={"Received Amount"} 
                  onChange={(e) => handleReceivedCashChange(e.target.value)}
                  value={tablePaymentInfo.receivedCash || ''}
                  />
              </div>
              <FormInput 
                  placeholder={"Discount"} 
                  onChange={(e) => handleDiscountChange(e.target.value)}
                  value={tablePaymentInfo.discount || 0}
                />
              <SelectField 
              options={[{label:"Cash",value:"Cash"},{label:"Card",value:"Card"},{label:"Jazz cash",value:"Jazz cash"}]} 
              value={tablePaymentInfo.paymentType || 'Cash'}
              onChange={(e) => handlePaymentTypeChange(e.target.value)}
              />
            </div> */}
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 h-[550px] overflow-y-auto">
              {activeTables
                .filter((table) =>
                  (table.name || table.tableName || `Table ${table.id}`)
                    .toLowerCase()
                    .includes(searchQuery.toLowerCase())
                )
                .map((table) => (
                  <TableCard
                    key={table.id}
                    table={table.items && table.items.length > 0 ? table : { ...table, items: [] }}
                    selected={selectedTable === table.id || selectedTable === table.tableId || selectedTable === table.value}
                    onPrint={() => handlePrint(table)}
                    onCancel={() => handleCancelTable(table.id, table.order_id)}
                    onComplete={() => handleComplete(table)}
                    onRemove={() => handleDeleteTable(table.id)}
                  />
                ))}
            </div>
          </div>
        ) : (
          <p className="text-sm text-gray-500">No active table orders. Activate or create a table to start.</p>
        )}
      </div>

      <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="mb-6 grid gap-4 lg:grid-cols-[1.4fr_0.9fr] lg:items-end">
          <div>
            <h3 className="text-xl font-semibold">All Branch Tables</h3>
            <p className="mt-2 text-sm text-gray-500">Add, activate, remove, and manage every table in your branch.</p>
          </div>
          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700">Create New Table</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={newTableName}
                onChange={(e) => setNewTableName(e.target.value)}
                placeholder="Table name"
                className="w-full rounded-2xl border border-gray-300 bg-white px-4 py-3 text-sm text-gray-700 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                disabled={loading}
              />
              <button
                type="button"
                onClick={handleCreateTable}
                disabled={loading}
                className="rounded-2xl bg-blue-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? 'Adding...' : 'Add'}
              </button>
            </div>
          </div>
        </div>

        <TablesList
          tables={branchTables}
          onActivate={handleActivateTable}
          onRemove={handleDeleteTable}
          loading={loading}
        />
      </div>
    </div>
  );
};

export default TablesSection;
